import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBrandingContext } from '../contexts/BrandingContext';

interface PublicProfile {
  full_name: string;
  current_job_title?: string;
  current_company?: string;
  location?: string;
  professional_summary?: string;
  years_of_experience?: number;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  personal_website?: string;
  profile_views: number;
  skills: Array<{
    name: string;
    proficiency?: string;
    years_experience?: number;
  }>;
  experience: Array<{
    company: string;
    title: string;
    duration: string;
    current: boolean;
    location?: string;
    employment_type?: string;
    description?: string;
    technologies_used?: string[];
  }>;
  education: Array<{
    institution: string;
    degree: string;
    field: string;
    year?: number;
  }>;
  projects?: Array<{
    name: string;
    description?: string;
    role?: string;
    technologies?: string[];
    url?: string;
    start_date?: string;
    end_date?: string;
    is_ongoing?: boolean;
  }>;
  certifications?: Array<{
    name: string;
    issuing_organization: string;
    issue_date?: string;
    expiration_date?: string;
    credential_url?: string;
    does_not_expire?: boolean;
  }>;
  languages?: Array<{
    language: string;
    proficiency: string;
    is_native?: boolean;
  }>;
  awards?: Array<{
    title: string;
    organization: string;
    date?: string;
    description?: string;
  }>;
  created_at: string;
}

const PublicProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { productName, loading: brandingLoading, branding } = useBrandingContext();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState({
    url: "",
    title: "",
  });

  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (!slug) {
        setError('Profile not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/v1/candidate/public/${slug}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('Profile not found or not public');
          } else {
            setError('Failed to load profile');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setProfile(data);
      } catch (err) {
        console.error('Error fetching public profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProfile();
  }, [slug]);

  // Wait for both profile and branding to load
  if (loading || brandingLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/profile/${slug}`;

    setShareData({
      url: shareUrl,
      title: profile?.full_name || "Profile",
    });

    setShowShareModal(true);
  };

  const formatEmploymentType = (value: string) => {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const handleCopyLink = async () => {
    const url = shareData.url;

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied!');
        return;
      } catch (clipboardError) {
        console.warn('Clipboard API failed, falling back to textarea copy', clipboardError);
      }
    }

    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('Link copied!');
      } else {
        window.prompt('Copy the link below:', url);
      }
    } catch (fallbackError) {
      console.warn('Fallback copy failed', fallbackError);
      window.prompt('Copy the link below:', url);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  if (error || !profile) {
    return (
      <>
        <Helmet>
          <title>Profile Not Found - {productName}</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Profile Not Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {error || 'The profile you are looking for does not exist or is not public.'}
            </p>
            <div className="mt-6">
              <a
                href="/"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{profile.full_name} - Public Profile | {productName}</title>
        <meta name="description" content={`${profile.full_name}'s professional profile on ${productName}. ${profile.current_job_title ? `${profile.current_job_title}` : 'Professional'} ${profile.current_company ? `at ${profile.current_company}` : ''}.`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center">
              <a href="/" className="text-indigo-600 hover:text-indigo-500 font-medium">
                ← Back to {productName}
              </a>
            </div>
          </div>
        </div>

        {/* Profile Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Profile Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold">{profile.full_name}</h1>
                  {profile.current_job_title && (
                    <p className="text-xl text-indigo-100 mt-2">
                      {profile.current_job_title}
                      {profile.current_company && (
                        <span className="text-indigo-200"> at {profile.current_company}</span>
                      )}
                    </p>
                  )}
                  {profile.location && (
                    <p className="text-indigo-200 mt-1 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {profile.location}
                    </p>
                  )}
                  {profile.years_of_experience && (
                    <p className="text-indigo-200 mt-1">
                      {profile.years_of_experience} years of experience
                    </p>
                  )}
                </div>

                {/* Social Links */}
                <div className="flex space-x-3">
                  {profile.linkedin_url && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                  )}
                  {profile.github_url && (
                    <a
                      href={profile.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                      </svg>
                    </a>
                  )}
                  {profile.portfolio_url && (
                    <a
                      href={profile.portfolio_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5.523 0-10-4.477-10-10s4.477-10 10-10 10 4.477 10 10z" />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={handleShare}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 100-6" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="p-6 space-y-8">
              {/* Professional Summary */}
              {profile.professional_summary && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">About</h2>
                  <p className="text-gray-700 leading-relaxed">{profile.professional_summary}</p>
                </div>
              )}

              {/* Skills */}
              {profile.skills && profile.skills.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Skills</h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {skill.name}
                        {skill.years_experience && (
                          <span className="text-indigo-600 ml-1">({skill.years_experience}y)</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {profile.experience && profile.experience.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Experience</h2>
                  <div className="space-y-6">
                    {profile.experience.map((exp, index) => (
                      <div key={index} className="border-l-2 border-indigo-200 pl-4">
                        <h3 className="font-medium text-gray-900">{exp.title}</h3>
                        <p className="text-indigo-600 font-medium">{exp.company}</p>
                        {exp.location && (
                          <p className="text-sm text-gray-600">{exp.location}</p>
                        )}
                        <p className="text-sm text-gray-600">
                          {exp.duration}
                          {exp.current && <span className="text-green-600 ml-2">• Current</span>}
                          {exp.employment_type && (
                            <span className="text-gray-500 ml-2">
                              • {formatEmploymentType(exp.employment_type)}
                            </span>
                          )}
                        </p>
                        {exp.description && (
                          <div className="mt-3 text-sm text-gray-700 whitespace-pre-line">
                            {exp.description}
                          </div>
                        )}
                        {exp.technologies_used && exp.technologies_used.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Technologies Used:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {exp.technologies_used.map((tech, techIndex) => (
                                <span
                                  key={techIndex}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects - Enhanced Layout */}
              {profile.projects && profile.projects.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Projects</h2>
                  <div className={`grid gap-6 ${profile.projects.length === 1
                    ? 'grid-cols-1 max-w-2xl'
                    : profile.projects.length === 2
                      ? 'grid-cols-1 md:grid-cols-2'
                      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                    }`}>
                    {profile.projects.map((project, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 flex-1 mr-2">{project.name}</h3>
                          {project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-500 flex-shrink-0"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                        {project.description && (
                          <p className="text-sm text-gray-700 mb-3 line-clamp-3">{project.description}</p>
                        )}
                        {project.role && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Role:</span> {project.role}
                          </p>
                        )}
                        {project.technologies && project.technologies.length > 0 && (
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1">
                              {project.technologies.slice(0, 4).map((tech, techIndex) => (
                                <span key={techIndex} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {tech}
                                </span>
                              ))}
                              {project.technologies.length > 4 && (
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                  +{project.technologies.length - 4} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {(project.start_date || project.end_date) && (
                          <div className="pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              {project.start_date && new Date(project.start_date).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short'
                              })}
                              {project.start_date && (project.end_date || project.is_ongoing) && ' - '}
                              {project.is_ongoing
                                ? 'Ongoing'
                                : project.end_date
                                  ? new Date(project.end_date).toLocaleDateString('en-US', {
                                    year: 'numeric', month: 'short'
                                  })
                                  : ''
                              }
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications - Enhanced Layout */}
              {profile.certifications && profile.certifications.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Certifications</h2>
                  <div className={`grid gap-4 ${profile.certifications.length === 1
                    ? 'grid-cols-1 max-w-lg'
                    : profile.certifications.length === 2
                      ? 'grid-cols-1 md:grid-cols-2'
                      : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
                    }`}>
                    {profile.certifications.map((cert, index) => (
                      <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-2">
                            <h3 className="font-semibold text-green-900 text-sm leading-tight">{cert.name}</h3>
                            <p className="text-sm text-green-700 font-medium">{cert.issuing_organization}</p>
                          </div>
                          <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                          </svg>
                        </div>
                        <div className="space-y-1">
                          {cert.issue_date && (
                            <p className="text-xs text-green-700">
                              <span className="font-medium">Issued:</span> {new Date(cert.issue_date).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short'
                              })}
                            </p>
                          )}
                          {cert.expiration_date && !cert.does_not_expire && (
                            <p className="text-xs text-green-700">
                              <span className="font-medium">Expires:</span> {new Date(cert.expiration_date).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short'
                              })}
                            </p>
                          )}
                          {cert.does_not_expire && (
                            <p className="text-xs text-green-700">
                              <span className="font-medium">Valid:</span> Does not expire
                            </p>
                          )}
                          {cert.credential_url && (
                            <a
                              href={cert.credential_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-green-600 hover:text-green-500 mt-2"
                            >
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Verify
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education */}
              {profile.education && profile.education.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Education</h2>
                  <div className="space-y-4">
                    {profile.education?.map((edu, index) => (
                      <div key={index} className="border-l-2 border-green-200 pl-4">
                        <h3 className="font-medium text-gray-900">{edu.degree}</h3>
                        <p className="text-green-600 font-medium">{edu.institution}</p>
                        <p className="text-sm text-gray-600">
                          {edu.field}
                          {edu.year && <span className="ml-2">• {edu.year}</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Awards & Achievements */}
              {profile.awards && profile.awards.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Awards & Achievements</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.awards?.map((award, index) => (
                      <div key={index} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-yellow-900">{award.title}</h3>
                            <p className="text-sm text-yellow-700 font-medium">{award.organization}</p>
                            {award.date && (
                              <p className="text-sm text-yellow-600 mt-1">
                                {new Date(award.date).toLocaleDateString('en-US', {
                                  year: 'numeric', month: 'long'
                                })}
                              </p>
                            )}
                            {award.description && (
                              <p className="text-sm text-yellow-800 mt-2">{award.description}</p>
                            )}
                          </div>
                          <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {profile.languages && profile.languages.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Languages</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {profile.languages?.map((lang, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <span className="font-medium text-blue-900">{lang.language}</span>
                          {lang.is_native && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Native
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-blue-700 capitalize">{lang.proficiency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {showShareModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-80">

                <h3 className="text-lg font-semibold mb-4 text-center">
                  Share Profile
                </h3>

                <div className="space-y-3">

                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareData.url)}`}
                    target="_blank"
                    className="block bg-green-500 text-white py-2 rounded text-center"
                  >
                    WhatsApp
                  </a>

                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData.url)}`}
                    target="_blank"
                    className="block bg-blue-600 text-white py-2 rounded text-center"
                  >
                    LinkedIn
                  </a>

                  <button
                    onClick={handleCopyLink}
                    className="w-full bg-gray-200 py-2 rounded"
                  >
                    Copy Link
                  </button>

                </div>

                <button
                  onClick={() => setShowShareModal(false)}
                  className="mt-4 text-sm text-gray-500 w-full"
                >
                  Close
                </button>

              </div>
            </div>
          )}
          {/* Footer */}
          <div className="text-center mt-8 text-gray-500">
            <p className="text-sm">
              Profile created on {productName} • {new Date(profile.created_at).toLocaleDateString()}
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default PublicProfilePage; 
